"use client";
import { Typography } from "@mui/material";
import React, { memo, useEffect, useState, useMemo } from "react";

const ONE_DAY_HOURS_MS = 24 * 60 * 60 * 1000;

function useCountdown(target: Date | null, isActive: boolean): number {
  const [remaining, setRemaining] = useState<number>(() => {
    if (!target || !isActive) return 0;
    return Math.max(0, target.getTime() - Date.now());
  });

  useEffect(() => {
    if (!target || !isActive) return;

    const tick = () => setRemaining(Math.max(0, target.getTime() - Date.now()));

    tick();
    const interval = setInterval(tick, 1000);

    return () => clearInterval(interval);
  }, [target, isActive]);

  return remaining;
}

function formatClock(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

type CountdownTimerProps = {
  endDate?: Date | string | null;
  isActive: boolean;
  small?: boolean;
};

const CountdownTimer = ({ endDate, isActive, small = false }: CountdownTimerProps) => {
  const targetDate = useMemo(() => {
    if (!endDate) return null;
    const parsedDate = new Date(endDate);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }, [endDate]);

  const remaining = useCountdown(targetDate, isActive);

  if (!targetDate || remaining >= ONE_DAY_HOURS_MS || remaining <= 0) return null;

  return (
    <Typography suppressHydrationWarning variant={small ? "body2" : "subtitle1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
      {formatClock(remaining)}
    </Typography>
  );
};

CountdownTimer.displayName = "CountdownTimer";

export default memo(CountdownTimer);
